import { escapeSql } from '@rdrive/helper-sql';
import { defineHierarchyChart } from '@rdrive/helper-chart-config';
import { ensureChartUsersTable } from '@/utils/chartUsers';
import { ITEMS_CHART, STATUS, STATUS_LEVEL } from './itemsHierarchy.js';

// Item breakdown by team -> user -> status, for the Teams (overview) page's
// StackedHorizontalBarChart (@rdrive/plugin-chart-stacked-bar). Since IA_ABSORPTION_PLAN Phase 5
// steps 5.2–5.4 a level table on `defineHierarchyChart` (@rdrive/helper-chart-config) — the
// generated queries equal the old hand-written ones (tooling/parity/hierarchy-chart/).
//
// Items with no team/user collapse into an 'unassigned' sentinel row, and the users join goes
// through the chart_users lookup because AssigneeUserIds is a JSON array string (built before the
// users level's data and filter queries). Scoping: the drill scope ('andIdIn', ./itemsHierarchy.js)
// keeps a status click inside the team / user the user navigated into.

const TEAMS = 'LEFT JOIN teams t ON i.AssigneeTeamId = t.id';
const USERS = `LEFT JOIN chart_users u ON i.AssigneeUserIds LIKE '%"' || u.id || '"%'`;

// Filter through the same teams/users joins the display queries use, so id type, casing, and
// un-assigned semantics always match what the user clicked on. Function conditions (the sentinel
// becomes IS NULL), so the escaping is ours (rule 6).
const teamCondition = (teamId) =>
  teamId === 'unassigned' ? 't.id IS NULL' : `CAST(t.id AS VARCHAR) = '${escapeSql(teamId)}'`;

const userCondition = (userId) =>
  userId === 'unassigned' ? 'u.id IS NULL' : `u.id = '${escapeSql(userId)}'`;

export function useTeamsUsersChart({ chartName, filterContext, conn }) {
  const ensureUsersTable = () => ensureChartUsersTable(conn);

  return defineHierarchyChart({
    chartName,
    filterContext,
    conn,
    ...ITEMS_CHART,
    levels: [
      // The status join comes before the LEFT JOINs, as it always did
      {
        level: 'teams', titleKey: 'teams',
        id: "COALESCE(t.id, 'unassigned')", title: "COALESCE(t.name, 'Un-assigned')", joins: [STATUS, TEAMS],
        filter: { joins: [TEAMS], where: [({ id }) => teamCondition(id)] },
      },
      // A user filter also holds the team selected one level up
      {
        level: 'users', titleKey: 'users',
        id: "COALESCE(u.id, 'unassigned')", title: "COALESCE(u.displayName, 'Un-assigned')", joins: [STATUS, USERS],
        before: ensureUsersTable,
        beforeFilter: ensureUsersTable,
        filter: {
          joins: [TEAMS, USERS],
          where: [({ drill }) => teamCondition(drill.teams?.id ?? null), ({ id }) => userCondition(id)],
        },
      },
      STATUS_LEVEL,
    ],
  });
}
